import { type Permission, canReadTaxAudit, canReadTaxNfe } from "@backchannel/tax-contracts";
import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import {
  type MouseEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAnnouncer } from "~/components/a11y/announcer";
import { MAIN_CONTENT_ID, SkipLink } from "~/components/a11y/skip-link";
import { Glyph } from "~/components/ui/glyph";
import { useI18n } from "~/i18n";
import type { MessageKey } from "~/i18n/messages/pt-BR";
import { roleLabelKey } from "~/lib/access/roles";
import { useSession } from "~/lib/access/session-context";
import { useOverviewQuery } from "~/lib/api/queries";
import { useAuth } from "~/lib/auth/auth-context";
import { reviewLandingForOverview } from "~/lib/domain/review-landing";
import {
  type CommandDestination,
  CommandSearch,
  SearchGlyph,
  getCommandShortcut,
  isCommandSearchShortcut,
} from "./command-search";
import { FocusLayoutContext, useFocusLayout } from "./focus-layout";
import { InsightsInbox } from "./insights-inbox";
import { type DestinationIconName, NavIcon, type NavIconName } from "./nav-icon";
import {
  ACCOUNT_LEGAL_PATH,
  ADMINISTRATION_DESTINATIONS,
  AGENT_DESTINATIONS,
  DAILY_TOOL_PATHS,
  DEVELOPER_DESTINATIONS,
  type DeveloperDestination,
  GOVERNANCE_PATHS,
  HOW_IT_WORKS_PATH,
  LAST_OPERATION_STORAGE_KEY,
  type OperationLocation,
  type OperationNavigateTarget,
  RULES_VERIFICATION_LINK,
  RULES_VERIFICATION_PATH,
  RULE_ROUTE_GUIDE_PATH,
  type ShellLink,
  type Workspace,
  authorizedOperationLocation,
  canAccessDevelopers,
  canonicalizeOperationLocation,
  developerHref,
  isDevelopersWorkspace,
  isPlatformAdmin,
  isReviewPath,
  isRulesPath,
  operationHref,
  operationNavigateTarget,
  operationalDestinationsForRole,
  parseStoredOperationLocation,
  pathMatchesDestination,
  shellLinkActive,
  workspaceFocusKey,
  workspaceForLocation,
} from "./nav-items";
import { ProfileAvatar } from "./profile-avatar";
import { RolePreviewPicker } from "./role-preview-picker";
import { SidebarSection } from "./sidebar-section";
import "./focus-layout.css";
import {
  readSidebarExpanded,
  useCompactNavigation,
  writeSidebarExpanded,
} from "./use-compact-navigation";

function DestinationGlyph({ name }: { name: NavIconName }) {
  return (
    <span className="sidenav-item__icon" aria-hidden="true">
      <NavIcon name={name} />
    </span>
  );
}

type SidebarEntry = {
  key: string;
  to: string;
  labelKey: MessageKey;
  icon: DestinationIconName;
  permission: Permission | null;
  active: boolean;
  nested?: boolean;
};

function NavigationFrame({
  compact,
  open,
  label,
  children,
}: {
  compact: boolean;
  open: boolean;
  label: string;
  children: ReactNode;
}) {
  if (!compact) return children;
  return (
    <dialog
      className="app-sidebar-modal"
      open={open}
      aria-modal={open ? true : undefined}
      aria-hidden={open ? undefined : true}
      aria-label={label}
    >
      {children}
    </dialog>
  );
}

const DEVELOPERS_TARGET = {
  to: "/settings",
  search: { workspace: "developers" },
  hash: "integrations",
} as const;

function readStoredOperationLocation(key: string | null): OperationLocation | null {
  if (!key) return null;
  try {
    return parseStoredOperationLocation(window.sessionStorage.getItem(key));
  } catch {
    return null;
  }
}

function writeStoredOperationLocation(key: string | null, location: OperationLocation) {
  if (!key) return;
  try {
    window.sessionStorage.setItem(key, JSON.stringify(location));
  } catch {
    // Session storage can be unavailable in a locked-down browser profile.
  }
}

export function AppShell({ children }: { children: ReactNode }) {
  const { t } = useI18n();
  const { session, permissions, can, effectiveRole, previewRole, canPreviewRoles, setPreviewRole } =
    useSession();
  const auth = useAuth();
  const navigate = useNavigate();
  const { active: calculatorFocus, exit: exitCalculator } = useFocusLayout();
  const [focusHeader, setFocusHeader] = useState<HTMLDivElement | null>(null);
  const { announce } = useAnnouncer();
  const compactNavigation = useCompactNavigation();
  const [navOpen, setNavOpen] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(readSidebarExpanded);
  const [profileOpen, setProfileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [developerSectionFocus, setDeveloperSectionFocus] = useState<
    DeveloperDestination["hash"] | null
  >(null);
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const hash = useRouterState({ select: (state) => state.location.hash });
  const locationSearch = useRouterState({ select: (state) => state.location.search });
  const searchStr = useRouterState({ select: (state) => state.location.searchStr });
  const routeStatus = useRouterState({ select: (state) => state.status });
  const canAudit = canReadTaxAudit(effectiveRole, permissions);
  const canDevelopers = canAccessDevelopers(can, effectiveRole);
  const overviewQuery = useOverviewQuery({ enabled: can("READ:TAX_OVERVIEW:ALL") });
  const reviewLanding = reviewLandingForOverview(overviewQuery.data, can);
  const railDestinations = operationalDestinationsForRole(effectiveRole);
  const platformAdmin = isPlatformAdmin(can, effectiveRole);
  const railItem = (to: string) => railDestinations.find((item) => item.to === to);
  /** Daily tools: always visible, in the order the finance team uses them. */
  const dailyTools: SidebarEntry[] = DAILY_TOOL_PATHS.flatMap((path) => {
    const item = railItem(path);
    if (!item) return [];
    const permission =
      item.to === "/invoices" && canReadTaxNfe(permissions) ? null : item.permission;
    const to =
      item.to === "/calculations/simulate" && !can("EXECUTE:TAX_CALCULATION:ALL")
        ? "/calculations"
        : item.to;
    const active =
      item.to === "/rules"
        ? isRulesPath(pathname) && !(platformAdmin && pathname === RULES_VERIFICATION_PATH)
        : pathMatchesDestination(pathname, item.to);
    const entry: SidebarEntry = { ...item, permission, key: item.to, to, active };
    if (item.to !== "/rules" || !platformAdmin) return [entry];
    // Regras → Verificação: the admin-only entry sits directly under Regras.
    return [
      entry,
      {
        ...RULES_VERIFICATION_LINK,
        key: RULES_VERIFICATION_LINK.id,
        permission: "READ:TAX_RULE:ALL",
        active: pathname === RULES_VERIFICATION_PATH,
        nested: true,
      },
    ];
  });
  /** Governança: Propostas · Documentos · Fontes · Publicações, open while work waits. */
  const governanceItems: SidebarEntry[] = GOVERNANCE_PATHS.flatMap((path) => {
    const item = railItem(path);
    return item
      ? [{ ...item, key: item.to, active: pathMatchesDestination(pathname, item.to) }]
      : [];
  });
  const governancePending = Boolean(reviewLanding.search.status);
  const adminEntries = (links: readonly ShellLink[], permission: Permission | null) =>
    links.map(
      (link): SidebarEntry => ({
        ...link,
        key: link.id,
        permission: link.to === ACCOUNT_LEGAL_PATH ? null : permission,
        active: shellLinkActive(pathname, link.to),
      }),
    );
  const agentItems = platformAdmin
    ? adminEntries(AGENT_DESTINATIONS, "ADMIN:TAX_PLATFORM:ALL")
    : [];
  const administrationItems = platformAdmin
    ? adminEntries(ADMINISTRATION_DESTINATIONS, "ADMIN:TAX_PLATFORM:ALL")
    : [];
  const requestedWorkspace = workspaceForLocation(pathname, searchStr);
  const workspace =
    requestedWorkspace === "developers" && !canDevelopers ? "operations" : requestedWorkspace;
  const isDevelopers = workspace === "developers";
  const isAccount =
    !isDevelopers &&
    ["/settings", "/settings/preferences", "/settings/passkeys", "/settings/password"].includes(
      pathname,
    );
  const shellModeLabel = isAccount
    ? t("auth.accountSettings")
    : isDevelopers
      ? t("nav.mode.developers")
      : t("app.name");
  const sectionHash = hash.replace(/^#/, "");
  const operationStorageKey = session
    ? [
        LAST_OPERATION_STORAGE_KEY,
        session.environment,
        session.principalId,
        ...[...session.organizationScopes].sort(),
      ].join(":")
    : null;
  const [operationState, setOperationState] = useState(() => ({
    key: operationStorageKey,
    location: readStoredOperationLocation(operationStorageKey),
  }));
  const lastOperation = operationState.key === operationStorageKey ? operationState.location : null;
  useEffect(() => {
    setOperationState({
      key: operationStorageKey,
      location: readStoredOperationLocation(operationStorageKey),
    });
  }, [operationStorageKey]);

  const operationsReturn = useMemo(
    () => authorizedOperationLocation(lastOperation, can, effectiveRole),
    [can, lastOperation, effectiveRole],
  );
  const operationsTarget = useMemo(
    () => operationNavigateTarget(operationsReturn),
    [operationsReturn],
  );
  const operationsHref = operationHref(operationsReturn);
  const profileRole = effectiveRole ? t(roleLabelKey(effectiveRole)) : "";
  const overlayOpen = !calculatorFocus && compactNavigation && navOpen;
  const navigationInert = calculatorFocus || searchOpen || (compactNavigation && !navOpen);
  const navToggleRef = useRef<HTMLButtonElement>(null);
  const sidebarToggleRef = useRef<HTMLButtonElement>(null);
  const searchTriggerRef = useRef<HTMLButtonElement>(null);
  const navigationRef = useRef<HTMLElement>(null);
  const profileRef = useRef<HTMLDivElement>(null);
  const profileTriggerRef = useRef<HTMLButtonElement>(null);
  const profileMenuRef = useRef<HTMLDialogElement>(null);
  const previousFocusKey = useRef(workspaceFocusKey(pathname, workspace));
  const routeNeedsFocus = useRef(calculatorFocus);
  const announceRef = useRef(announce);
  const translateRef = useRef(t);
  announceRef.current = announce;
  translateRef.current = t;

  useEffect(() => {
    const root = document.documentElement;
    root.classList.add("tax-workspace-active");

    return () => root.classList.remove("tax-workspace-active");
  }, []);

  const commandShortcut = useMemo(() => getCommandShortcut(), []);
  const modifierLabel = commandShortcut.startsWith("⌘") ? "⌘" : "Ctrl+";

  const closeNavigation = useCallback(
    (restoreFocus = true) => {
      setNavOpen(false);
      if (restoreFocus && compactNavigation) {
        requestAnimationFrame(() => navToggleRef.current?.focus({ preventScroll: true }));
      }
    },
    [compactNavigation],
  );

  const toggleSidebarExpanded = useCallback(() => {
    setSidebarExpanded((current) => {
      const next = !current;
      writeSidebarExpanded(next);
      return next;
    });
  }, []);

  const closeProfile = useCallback((restoreFocus = true) => {
    setProfileOpen(false);
    if (restoreFocus) {
      requestAnimationFrame(() => profileTriggerRef.current?.focus({ preventScroll: true }));
    }
  }, []);

  function closeNavigationOnPrimaryClick(
    event: MouseEvent<HTMLAnchorElement>,
    sectionHash?: DeveloperDestination["hash"],
  ) {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }
    if (sectionHash) setDeveloperSectionFocus(sectionHash);
    closeNavigation();
  }

  function closeNavigationOnDeveloperClick(
    event: MouseEvent<HTMLAnchorElement>,
    sectionHash: DeveloperDestination["hash"],
  ) {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }
    event.preventDefault();
    setDeveloperSectionFocus(sectionHash);
    closeNavigation();
    // Keep the current Developers search (including request filters) while
    // changing only the section anchor. This avoids a full document reload
    // when a diagnostic view is opened from the drawer.
    void navigate({
      to: "/settings",
      search: { ...(isDevelopers ? locationSearch : {}), workspace: "developers" },
      hash: sectionHash,
      replace: true,
    } as never);
  }

  function selectProfileWorkspace(
    event: MouseEvent<HTMLAnchorElement>,
    target: OperationNavigateTarget | typeof DEVELOPERS_TARGET,
    targetWorkspace: Workspace,
  ) {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }
    event.preventDefault();
    if (workspace === targetWorkspace && !isAccount) {
      closeProfile();
      return;
    }
    closeProfile(false);
    setNavOpen(false);
    void navigate(target as never);
  }

  const openSearch = useCallback(() => {
    if (session === null) return;
    setNavOpen(false);
    setProfileOpen(false);
    setSearchOpen(true);
  }, [session]);

  const closeSearch = useCallback(() => {
    setSearchOpen(false);
    window.requestAnimationFrame(() => {
      const target = calculatorFocus
        ? document.getElementById("calculator-focus-title")
        : searchTriggerRef.current;
      target?.focus({ preventScroll: true });
    });
  }, [calculatorFocus]);

  useEffect(() => {
    if (session === null || isDevelopersWorkspace(pathname, searchStr)) {
      return;
    }
    const canonical = canonicalizeOperationLocation({ pathname, search: searchStr, hash });
    if (!canonical) return;
    const authorized = authorizedOperationLocation(canonical, can);
    if (authorized.pathname !== canonical.pathname) return;
    setOperationState({ key: operationStorageKey, location: canonical });
    writeStoredOperationLocation(operationStorageKey, canonical);
  }, [can, hash, operationStorageKey, pathname, searchStr, session]);

  useEffect(() => {
    const handleCommandSearch = (event: KeyboardEvent) => {
      const shortcut = isCommandSearchShortcut(event) && !event.repeat;
      if (!shortcut || event.isComposing || event.defaultPrevented) return;
      event.preventDefault();
      if (searchOpen) {
        // The same chord closes the palette, as in Linear and Vercel.
        closeSearch();
        return;
      }
      const blockingDialog = document.querySelector(
        'dialog[open][aria-modal="true"], [role="dialog"][aria-modal="true"]',
      );
      if (blockingDialog && !blockingDialog.classList.contains("command-search")) return;
      openSearch();
    };

    window.addEventListener("keydown", handleCommandSearch);
    return () => window.removeEventListener("keydown", handleCommandSearch);
  }, [closeSearch, openSearch, searchOpen]);

  useEffect(() => {
    if (!compactNavigation) setNavOpen(false);
  }, [compactNavigation]);

  useEffect(() => {
    const handleSidebarShortcut = (event: KeyboardEvent) => {
      if (calculatorFocus) return;
      const shortcut =
        (event.metaKey || event.ctrlKey) &&
        !event.altKey &&
        !event.shiftKey &&
        !event.repeat &&
        event.key.toLowerCase() === "b";
      if (!shortcut || event.isComposing || event.defaultPrevented) return;
      event.preventDefault();
      if (compactNavigation) {
        setProfileOpen(false);
        setSearchOpen(false);
        setNavOpen((open) => !open);
        return;
      }
      toggleSidebarExpanded();
    };

    window.addEventListener("keydown", handleSidebarShortcut);
    return () => window.removeEventListener("keydown", handleSidebarShortcut);
  }, [calculatorFocus, compactNavigation, toggleSidebarExpanded]);

  useEffect(() => {
    if (!searchOpen) return;

    const background = [
      document.querySelector<HTMLElement>(".app-topbar"),
      document.getElementById(MAIN_CONTENT_ID),
    ].filter((element): element is HTMLElement => Boolean(element));
    const previousOverflow = document.body.style.overflow;
    for (const element of background) element.setAttribute("inert", "");
    document.body.style.overflow = "hidden";

    return () => {
      for (const element of background) element.removeAttribute("inert");
      document.body.style.overflow = previousOverflow;
    };
  }, [searchOpen]);

  useEffect(() => {
    // Record the visit before scheduling focus. A quick A → B → A must focus
    // A's new landmark even when B's animation frame was cancelled. Keep the
    // pending intent through loading; same-page data paints never set it.
    // Workspace is part of the key because account Settings → Developers keeps
    // the pathname; filters and hashes intentionally remain outside the key.
    const focusKey = workspaceFocusKey(pathname, workspace);
    if (previousFocusKey.current !== focusKey) {
      previousFocusKey.current = focusKey;
      routeNeedsFocus.current = true;
    }
    if (routeStatus !== "idle" || !routeNeedsFocus.current) return;

    let child = 0;
    const frame = window.requestAnimationFrame(() => {
      child = window.requestAnimationFrame(() => {
        const main = document.getElementById(MAIN_CONTENT_ID);
        if (!main) return;
        routeNeedsFocus.current = false;
        const heading = calculatorFocus ? document.getElementById("calculator-focus-title") : null;
        (heading ?? main)?.focus({ preventScroll: true });
        main?.scrollTo({ top: 0 });
        const title = (heading ?? main?.querySelector("h1"))?.textContent?.trim();
        if (calculatorFocus) announceRef.current(translateRef.current("calculatorFocus.announce"));
        else if (title) announceRef.current(translateRef.current("a11y.routeAnnounce", { title }));
      });
    });

    return () => {
      window.cancelAnimationFrame(frame);
      window.cancelAnimationFrame(child);
    };
  }, [calculatorFocus, pathname, routeStatus, workspace]);

  // Route transitions close transient surfaces even when the router reports
  // an intermediate non-idle status. This prevents a mobile drawer or profile
  // menu from remaining above the newly rendered page.
  // biome-ignore lint/correctness/useExhaustiveDependencies: pathname is the intentional trigger for closing transient surfaces after navigation.
  useEffect(() => {
    setNavOpen(false);
    setProfileOpen(false);
    setSearchOpen(false);
  }, [pathname]);

  useEffect(() => {
    const handleRouteEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented || event.isComposing) {
        return;
      }
      if (searchOpen || navOpen || profileOpen) return;
      // Native explanation popovers own Escape; let the browser dismiss them.
      if (document.querySelector('dialog[open], [aria-modal="true"], [popover]:popover-open'))
        return;

      if (calculatorFocus) {
        event.preventDefault();
        exitCalculator();
        return;
      }

      const target = event.target as HTMLElement | null;
      if (target?.matches("input, textarea, select") || target?.isContentEditable) {
        return;
      }

      if (isDevelopers) {
        event.preventDefault();
        void navigate(operationsTarget as never);
        return;
      }

      if (routeStatus !== "idle") return;

      const runDetail = pathname.match(/^\/sources\/([^/]+)\/runs\/[^/]+\/?$/);
      if (runDetail?.[1]) {
        event.preventDefault();
        void navigate({
          to: "/sources/$sourceId",
          params: { sourceId: runDetail[1] },
          search: { tab: "runs" },
          replace: true,
        });
        return;
      }

      if (/^\/calculations\/tasks\/[^/]+\/?$/.test(pathname)) {
        event.preventDefault();
        void navigate({ to: "/calculations/tasks" as never, replace: true });
        return;
      }

      if (
        pathname.startsWith("/rules/") &&
        pathname !== "/rules/library" &&
        pathname !== "/rules/guide"
      ) {
        event.preventDefault();
        void navigate({ to: "/rules/library", search: { kind: "FISCAL" }, replace: true });
        return;
      }
      if (pathname === "/rules/library") return;

      const detail = pathname.match(
        /^\/(evidence|proposals|rulesets|rules|calculations|sources|audit)\/[^/]+/,
      );
      if (!detail?.[1]) return;
      event.preventDefault();
      void navigate({ to: `/${detail[1]}` as never, replace: true });
    };

    document.addEventListener("keydown", handleRouteEscape, true);
    return () => document.removeEventListener("keydown", handleRouteEscape, true);
  }, [
    calculatorFocus,
    exitCalculator,
    isDevelopers,
    navOpen,
    navigate,
    operationsTarget,
    pathname,
    profileOpen,
    routeStatus,
    searchOpen,
  ]);

  useEffect(() => {
    if (!overlayOpen) return;

    // The drawer is mounted while closed. Under a busy main thread, a fixed
    // timeout can therefore run against hidden DOM before the open commit
    // lands; retry by animation frame until the sheet is actually visible.
    let frame = 0;
    let attempts = 0;
    const focusFirstNavigationItem = () => {
      const navigation = navigationRef.current;
      const first =
        navigation?.querySelector<HTMLElement>(".app-sidebar__close") ??
        navigation?.querySelector<HTMLElement>(".sidenav-item[href]:not([aria-disabled='true'])") ??
        navigation?.querySelector<HTMLElement>("a[href]:not([aria-disabled='true'])") ??
        navigation?.querySelector<HTMLElement>(
          "button:not([disabled]):not([aria-disabled='true']):not(.app-sidebar__close)",
        );
      const fallback = navigation?.querySelector<HTMLElement>(".app-sidebar__close");
      const style = navigation ? window.getComputedStyle(navigation) : null;
      const ready =
        navigation?.dataset.open === "true" &&
        style?.display !== "none" &&
        style?.visibility === "visible" &&
        style.pointerEvents !== "none" &&
        navigation.getBoundingClientRect().height > 0;

      if (ready && (first ?? fallback)) {
        (first ?? fallback)?.focus({ preventScroll: true });
        return;
      }

      if (attempts < 60) {
        attempts += 1;
        frame = window.requestAnimationFrame(focusFirstNavigationItem);
      }
    };

    frame = window.requestAnimationFrame(focusFirstNavigationItem);
    return () => window.cancelAnimationFrame(frame);
  }, [overlayOpen]);

  useEffect(() => {
    if (!overlayOpen) return;
    const topbar = document.querySelector<HTMLElement>(".app-topbar");
    const main = document.getElementById(MAIN_CONTENT_ID);
    const previousOverflow = document.body.style.overflow;
    topbar?.setAttribute("inert", "");
    main?.setAttribute("inert", "");
    document.body.style.overflow = "hidden";
    return () => {
      topbar?.removeAttribute("inert");
      main?.removeAttribute("inert");
      document.body.style.overflow = previousOverflow;
    };
  }, [overlayOpen]);

  useEffect(() => {
    if (!profileOpen) return;
    const frame = window.requestAnimationFrame(() =>
      profileMenuRef.current
        ?.querySelector<HTMLElement>("a[href], select, button:not([disabled])")
        ?.focus({ preventScroll: true }),
    );

    return () => window.cancelAnimationFrame(frame);
  }, [profileOpen]);

  useEffect(() => {
    if (!developerSectionFocus || !isDevelopers || sectionHash !== developerSectionFocus) return;

    let frame = 0;
    let child = 0;
    let attempts = 0;
    const focusSection = () => {
      const target = document.getElementById(developerSectionFocus);
      if (!(target instanceof HTMLElement)) {
        if (attempts < 60) {
          attempts += 1;
          frame = window.requestAnimationFrame(focusSection);
        }
        return;
      }

      // The Settings section owns the target markup. Keep the shell resilient
      // while that content is loading, and make the anchor target keyboard
      // addressable for screen-reader users.
      if (!target.hasAttribute("tabindex")) target.tabIndex = -1;
      target.focus({ preventScroll: true });
      target.scrollIntoView({ block: "start" });
      setDeveloperSectionFocus(null);
    };

    frame = window.requestAnimationFrame(() => {
      child = window.requestAnimationFrame(focusSection);
    });

    return () => {
      window.cancelAnimationFrame(frame);
      window.cancelAnimationFrame(child);
    };
  }, [developerSectionFocus, isDevelopers, sectionHash]);

  // Keep both transient menus dismissible without relying on a pointer device.
  // The trigger regains focus after Escape or an explicit close so keyboard
  // users never land at the beginning of the page unexpectedly.
  useEffect(() => {
    if (!overlayOpen && !profileOpen) return;

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      const targetElement = target instanceof Element ? target : null;
      if (
        overlayOpen &&
        !navigationRef.current?.contains(target) &&
        !navToggleRef.current?.contains(target) &&
        // Let the scrim's click handler close the drawer and restore focus.
        // Dismissing on pointerdown would unmount the button before click.
        !targetElement?.closest(".app-sidebar__scrim")
      ) {
        closeNavigation(false);
      }
      if (profileOpen && !profileRef.current?.contains(target)) {
        closeProfile();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        if (event.defaultPrevented || event.isComposing) return;
        event.preventDefault();
        if (profileOpen) {
          closeProfile();
          return;
        }
        if (overlayOpen) closeNavigation();
        return;
      }

      if (event.key !== "Tab") return;

      const focusables = (container: Element | null) =>
        Array.from(
          container?.querySelectorAll<HTMLElement>(
            "a[href], summary, button:not([disabled]), select:not([disabled]), input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])",
          ) ?? [],
        ).filter((element) => {
          const style = window.getComputedStyle(element);
          // Links inside a folded sidebar group are not reachable by Tab.
          return (
            !element.hidden &&
            style.display !== "none" &&
            style.visibility !== "hidden" &&
            !element.closest("details:not([open]) > :not(summary)") &&
            element.getAttribute("aria-disabled") !== "true"
          );
        });

      if (profileOpen) {
        const menu = profileMenuRef.current;
        const items = focusables(menu);
        if (!menu?.contains(document.activeElement) || items.length === 0) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (!first || !last) return;
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus({ preventScroll: true });
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus({ preventScroll: true });
        }
        return;
      }

      if (!overlayOpen) return;
      const drawer = navigationRef.current;
      const items = focusables(drawer);
      if (!drawer?.contains(document.activeElement) || items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus({ preventScroll: true });
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus({ preventScroll: true });
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [closeNavigation, closeProfile, overlayOpen, profileOpen]);

  const toggleProfile = () => {
    setNavOpen(false);
    setSearchOpen(false);
    if (profileOpen) closeProfile();
    else setProfileOpen(true);
  };

  const toggleNavigation = () => {
    closeProfile(false);
    setSearchOpen(false);
    if (navOpen) closeNavigation(false);
    else setNavOpen(true);
  };

  const allowedEntry = (item: { permission: Permission | null }) =>
    item.permission === null || can(item.permission);
  const toCommand =
    (group: string) =>
    (item: {
      to: string;
      labelKey: MessageKey;
      icon: DestinationIconName;
    }): CommandDestination => ({
      group,
      label: t(item.labelKey),
      to: item.to,
      icon: item.icon,
    });
  /** The palette mirrors the sidebar groups, plus the Regras sub-pages. */
  const commandDestinations: CommandDestination[] = session
    ? [
        ...dailyTools
          .filter((item) => !item.nested && allowedEntry(item))
          .map(toCommand(t("nav.areas"))),
        ...railDestinations
          .filter(
            (item) =>
              (item.to === "/rules/library" || item.to === "/catalog/ncm") && allowedEntry(item),
          )
          .map(toCommand(t("shell.nav.rules"))),
        ...(can("READ:TAX_RULE:ALL") && railItem("/rules")
          ? [
              {
                to: "/rules/coverage",
                labelKey: "coverage.tab" as MessageKey,
                icon: "rules" as const,
              },
              { to: "/rules/gaps", labelKey: "gaps.tab" as MessageKey, icon: "rules" as const },
            ].map(toCommand(t("shell.nav.rules")))
          : []),
        ...(platformAdmin ? [toCommand(t("shell.nav.rules"))(RULES_VERIFICATION_LINK)] : []),
        ...governanceItems.filter(allowedEntry).map(toCommand(t("nav.governance"))),
        ...agentItems.map(toCommand(t("shell.nav.agentsGroup"))),
        ...(platformAdmin
          ? administrationItems
          : adminEntries(
              ADMINISTRATION_DESTINATIONS.filter((link) => link.to === ACCOUNT_LEGAL_PATH),
              null,
            )
        ).map(toCommand(t("nav.admin.module"))),
        ...(canAudit
          ? [
              {
                group: t("nav.developers.dock"),
                label: t("nav.audit"),
                to: "/audit",
                icon: "audit" as DestinationIconName,
              },
            ]
          : []),
        {
          group: t("auth.accountSection"),
          label: t("auth.accountSettings"),
          to: "/settings",
          icon: "settings" as DestinationIconName,
        },
      ]
    : [];

  const collapsedRail = !compactNavigation && !sidebarExpanded;
  /** Ajuda: how the engine works for platform admins, the route guide for rule readers. */
  const helpLink =
    session === null
      ? null
      : platformAdmin
        ? HOW_IT_WORKS_PATH
        : can("READ:TAX_RULE:ALL")
          ? RULE_ROUTE_GUIDE_PATH
          : null;

  function renderEntry(item: SidebarEntry) {
    const label = t(item.labelKey);
    const allowed = allowedEntry(item);
    const lockDescriptionId = `nav-lock-${item.key.replace(/[^a-zA-Z0-9]+/g, "-")}`;
    const className = item.nested ? "sidenav-item sidenav-item--nested" : "sidenav-item";

    if (session === null) {
      return (
        <button
          type="button"
          key={item.key}
          className={className}
          data-access="unavailable"
          aria-disabled="true"
          aria-label={label}
          title={`${label}. ${t("access.unavailable.title")}`}
        >
          <DestinationGlyph name={item.icon} />
          <span className="sidenav-item__label">{label}</span>
          <span className="sr-only">{t("access.unavailable.title")}</span>
        </button>
      );
    }

    if (!allowed && item.permission !== null) {
      return (
        <button
          type="button"
          key={item.key}
          className={className}
          data-access="denied"
          aria-disabled="true"
          aria-label={label}
          aria-describedby={lockDescriptionId}
          title={`${label}. ${t("access.denied.short", { permission: item.permission })}`}
        >
          <DestinationGlyph name={item.icon} />
          <span className="sidenav-item__label">{label}</span>
          <span className="sidenav-item__lock" aria-hidden="true">
            <Glyph name="lock" />
          </span>
          <span className="sr-only" id={lockDescriptionId}>
            {t("nav.locked")}: {item.permission}
          </span>
        </button>
      );
    }

    return (
      <Link
        key={item.key}
        to={item.to as never}
        search={{} as never}
        activeOptions={{ exact: true, includeSearch: false }}
        className={className}
        title={collapsedRail ? label : undefined}
        aria-current={item.active ? "page" : undefined}
        data-active={item.active ? "true" : undefined}
        onClick={closeNavigationOnPrimaryClick}
      >
        <DestinationGlyph name={item.icon} />
        <span className="sidenav-item__label">{label}</span>
      </Link>
    );
  }

  const sectionProps = {
    revealOnly: collapsedRail,
    onOpen: () => {
      if (!compactNavigation && !sidebarExpanded) {
        setSidebarExpanded(true);
        writeSidebarExpanded(true);
      }
    },
  };

  return (
    <div
      className="app-shell"
      data-layout={calculatorFocus ? "calculator-focus" : undefined}
      data-mode={workspace}
      data-workspace={workspace}
      data-sidebar-collapsed={collapsedRail ? "true" : undefined}
      data-role-preview={previewRole ?? undefined}
    >
      <SkipLink />

      <header className="app-topbar">
        <div className="app-topbar__row">
          {calculatorFocus ? (
            <>
              <button
                type="button"
                className="btn btn--ghost focus-layout__back"
                onClick={exitCalculator}
              >
                <NavIcon name="back" />
                <span>{t("calculatorFocus.back")}</span>
              </button>
              <div className="focus-layout__header" ref={setFocusHeader} />
            </>
          ) : compactNavigation ? (
            <div className="app-topbar__leading">
              <button
                type="button"
                ref={navToggleRef}
                className="app-topbar__menu"
                data-open={navOpen ? "true" : "false"}
                aria-expanded={navOpen}
                aria-controls="primary-navigation"
                aria-label={navOpen ? t("nav.close") : t("nav.open")}
                title={navOpen ? t("nav.close") : t("nav.open")}
                onClick={toggleNavigation}
              >
                <span className="app-topbar__menu-icon" aria-hidden="true">
                  <span />
                  <span />
                  <span />
                </span>
              </button>
              <Link to="/" className="shell-brand" aria-label={t("common.workspaceHome")}>
                <img
                  className="shell-brand__mark"
                  src="/backchannel-mark.svg"
                  alt=""
                  width="20"
                  height="22"
                />
                <span className="shell-brand__copy">
                  <strong>{t("app.name")}</strong>
                </span>
              </Link>
            </div>
          ) : null}

          {!calculatorFocus && (
            <button
              type="button"
              ref={searchTriggerRef}
              className="app-topbar__search"
              aria-label={t("commandSearch.trigger")}
              aria-controls="command-search-dialog"
              aria-haspopup="dialog"
              aria-expanded={searchOpen}
              aria-keyshortcuts="Meta+K Control+K"
              disabled={session === null}
              onClick={openSearch}
            >
              <SearchGlyph />
              <span>{t("shell.search.trigger")}</span>
              <kbd>{commandShortcut}</kbd>
            </button>
          )}

          <div className="app-topbar__trailing">
            {session ? <InsightsInbox /> : null}
            {session && (
              <div className="app-topbar__profile" ref={profileRef}>
                <button
                  type="button"
                  ref={profileTriggerRef}
                  className="app-topbar__account"
                  title={session.email}
                  aria-label={t("auth.accountTrigger", {
                    name: session.displayName ?? session.userId,
                  })}
                  aria-haspopup="dialog"
                  aria-expanded={profileOpen}
                  aria-controls="profile-menu"
                  onClick={toggleProfile}
                >
                  <ProfileAvatar
                    name={session.displayName ?? session.userId}
                    avatarUrl={session.avatarUrl}
                    className="app-topbar__avatar"
                  />
                  <span className="app-topbar__account-copy">
                    <strong>{session.displayName ?? session.userId}</strong>
                    {previewRole ? (
                      <small className="app-topbar__account-preview">
                        {t("nav.viewAs.acting", { role: t(roleLabelKey(previewRole)) })}
                      </small>
                    ) : null}
                  </span>
                  <svg
                    className="app-topbar__account-chevron"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path d="m7 9 5 5 5-5" />
                  </svg>
                </button>

                {profileOpen && (
                  <dialog
                    open
                    className="app-profile-menu"
                    id="profile-menu"
                    aria-label={t("auth.accountMenu")}
                    ref={profileMenuRef}
                  >
                    <div className="app-profile-menu__identity">
                      <ProfileAvatar
                        name={session.displayName ?? session.userId}
                        avatarUrl={session.avatarUrl}
                        className="app-topbar__avatar"
                      />
                      <span className="app-profile-menu__identity-copy">
                        <strong>{session.displayName ?? session.userId}</strong>
                        <span data-replay-mask>{session.email}</span>
                        <small>
                          {previewRole
                            ? t("nav.viewAs.acting", { role: t(roleLabelKey(previewRole)) })
                            : profileRole}
                        </small>
                      </span>
                    </div>

                    <nav
                      className="app-profile-menu__account"
                      aria-label={t("auth.accountSection")}
                    >
                      <Link
                        to="/settings"
                        search={{}}
                        className="app-profile-menu__item"
                        onClick={() => setProfileOpen(false)}
                      >
                        <NavIcon name="settings" />
                        <span>{t("auth.accountSettings")}</span>
                      </Link>
                    </nav>

                    {canPreviewRoles && <RolePreviewPicker onSelect={() => closeProfile(true)} />}

                    <button
                      type="button"
                      className="app-profile-menu__item app-profile-menu__item--danger"
                      onClick={() => {
                        closeProfile(false);
                        auth.signOut();
                      }}
                    >
                      <svg viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M14 5h5v14h-5M10 12h9M10 12l3-3M10 12l3 3M5 5h5M5 19h5" />
                      </svg>
                      <span>{t("auth.signOut")}</span>
                    </button>
                  </dialog>
                )}
              </div>
            )}
          </div>
        </div>
        {previewRole ? (
          <output className="role-preview-banner">
            <span className="role-preview-banner__copy">
              <NavIcon name="users" />
              <span>{t("nav.viewAs.banner", { role: t(roleLabelKey(previewRole)) })}</span>
            </span>
            <button
              type="button"
              className="role-preview-banner__exit"
              onClick={() => setPreviewRole(null)}
            >
              {t("nav.viewAs.exit")}
            </button>
          </output>
        ) : null}
      </header>

      {overlayOpen ? (
        <button
          type="button"
          className="app-sidebar__scrim"
          aria-label={t("nav.close")}
          onClick={() => closeNavigation()}
          tabIndex={-1}
        />
      ) : null}

      <NavigationFrame compact={compactNavigation} open={navOpen} label={t("nav.menu")}>
        <nav
          className="app-sidebar"
          id="primary-navigation"
          aria-label={t("nav.menu")}
          data-workspace={workspace}
          data-open={compactNavigation ? (navOpen ? "true" : "false") : "true"}
          data-collapsed={collapsedRail ? "true" : undefined}
          inert={navigationInert ? true : undefined}
          aria-hidden={calculatorFocus || undefined}
          ref={navigationRef}
        >
          <div className="app-sidebar__head">
            <Link
              to="/"
              className="shell-brand"
              aria-label={t("common.workspaceHome")}
              title={collapsedRail ? t("app.name") : undefined}
              onClick={closeNavigationOnPrimaryClick}
            >
              <img
                className="shell-brand__mark"
                src="/backchannel-mark.svg"
                alt=""
                width="20"
                height="22"
              />
              <span className="shell-brand__copy">
                <strong>{t("app.name")}</strong>
                <small>{t("app.org")}</small>
              </span>
            </Link>
            {!compactNavigation ? (
              <button
                type="button"
                ref={sidebarToggleRef}
                className="app-sidebar__toggle"
                aria-expanded={sidebarExpanded}
                aria-controls="primary-navigation"
                aria-keyshortcuts="Meta+B Control+B"
                aria-label={sidebarExpanded ? t("shell.nav.collapse") : t("shell.nav.expand")}
                data-tooltip={t(
                  sidebarExpanded ? "shell.nav.collapseTooltip" : "shell.nav.expandTooltip",
                  { shortcut: `${modifierLabel}B` },
                )}
                onClick={toggleSidebarExpanded}
              >
                <NavIcon name="panel" />
              </button>
            ) : null}
            {compactNavigation ? (
              <button
                type="button"
                className="app-sidebar__close"
                aria-label={t("nav.close")}
                title={t("nav.close")}
                onClick={() => closeNavigation()}
              >
                <span className="app-sidebar__close-icon" aria-hidden="true">
                  <span />
                  <span />
                </span>
              </button>
            ) : null}
          </div>

          <div className="app-sidebar__body scroll-thin">
            {isAccount ? (
              <div className="sidenav-list">
                <a
                  href={operationsHref}
                  className="sidenav-item sidenav-item--back"
                  title={collapsedRail ? t("auth.backToOperations") : undefined}
                  onClick={(event) => selectProfileWorkspace(event, operationsTarget, "operations")}
                >
                  <DestinationGlyph name="back" />
                  <span className="sidenav-item__label">{t("auth.backToOperations")}</span>
                </a>
                <p className="sidenav-heading">{shellModeLabel}</p>
                {(
                  [
                    { section: "profile", path: "/settings", icon: "users" },
                    { section: "preferences", path: "/settings/preferences", icon: "settings" },
                    { section: "passkeys", path: "/settings/passkeys", icon: "key" },
                    { section: "password", path: "/settings/password", icon: "key" },
                  ] as const
                ).map((item) => (
                  <Link
                    key={item.section}
                    to={item.path}
                    search={{}}
                    hash=""
                    className="sidenav-item"
                    title={collapsedRail ? t(`account.section.${item.section}`) : undefined}
                    aria-current={pathname === item.path ? "page" : undefined}
                    data-active={pathname === item.path ? "true" : undefined}
                    onClick={() => closeNavigation(false)}
                  >
                    <DestinationGlyph name={item.icon} />
                    <span className="sidenav-item__label">
                      {t(`account.section.${item.section}`)}
                    </span>
                  </Link>
                ))}
              </div>
            ) : isDevelopers ? (
              <div className="sidenav-list" data-workspace="developers">
                <a
                  href={operationsHref}
                  className="sidenav-item sidenav-item--back"
                  title={collapsedRail ? t("auth.backToOperations") : undefined}
                  onClick={(event) => selectProfileWorkspace(event, operationsTarget, "operations")}
                >
                  <DestinationGlyph name="back" />
                  <span className="sidenav-item__label">{t("nav.operationsHome")}</span>
                </a>
                <p className="sidenav-heading">{t("nav.developers.section")}</p>
                {DEVELOPER_DESTINATIONS.map((item) => {
                  const label = t(item.labelKey);
                  const active = sectionHash === item.hash;
                  return (
                    <a
                      key={item.id}
                      href={developerHref(item.hash)}
                      className="sidenav-item"
                      title={collapsedRail ? label : undefined}
                      aria-current={active ? "page" : undefined}
                      data-active={active ? "true" : undefined}
                      data-workspace-destination={item.id}
                      onClick={(event) => closeNavigationOnDeveloperClick(event, item.hash)}
                    >
                      <DestinationGlyph name={item.icon} />
                      <span className="sidenav-item__label">{label}</span>
                    </a>
                  );
                })}
                {canAudit && (
                  <Link
                    to="/audit"
                    className="sidenav-item"
                    title={collapsedRail ? t("nav.audit") : undefined}
                    data-workspace-destination="audit"
                    aria-current={pathname.startsWith("/audit") ? "page" : undefined}
                    data-active={pathname.startsWith("/audit") ? "true" : undefined}
                    onClick={() => closeNavigation(false)}
                  >
                    <DestinationGlyph name="audit" />
                    <span className="sidenav-item__label">{t("nav.audit")}</span>
                  </Link>
                )}
              </div>
            ) : (
              <>
                {dailyTools.length > 0 ? (
                  <div className="sidenav-list">{dailyTools.map(renderEntry)}</div>
                ) : null}

                {session && governanceItems.length > 0 ? (
                  <SidebarSection
                    id="governance"
                    label={t("nav.governance")}
                    icon="governance"
                    activeKey={isReviewPath(pathname) ? pathname : undefined}
                    containsActive={governanceItems.some((item) => item.active)}
                    defaultOpen={governancePending}
                    {...sectionProps}
                  >
                    {governanceItems.map(renderEntry)}
                  </SidebarSection>
                ) : null}

                {agentItems.length > 0 ? (
                  <SidebarSection
                    id="agents"
                    label={t("shell.nav.agentsGroup")}
                    icon="automation"
                    activeKey={agentItems.some((item) => item.active) ? pathname : undefined}
                    containsActive={agentItems.some((item) => item.active)}
                    {...sectionProps}
                  >
                    {agentItems.map(renderEntry)}
                  </SidebarSection>
                ) : null}

                {administrationItems.length > 0 ? (
                  <SidebarSection
                    id="administration"
                    label={t("nav.admin.module")}
                    icon="admin"
                    activeKey={
                      administrationItems.some((item) => item.active) ? pathname : undefined
                    }
                    containsActive={administrationItems.some((item) => item.active)}
                    {...sectionProps}
                  >
                    {administrationItems.map(renderEntry)}
                  </SidebarSection>
                ) : null}

                {canDevelopers ? (
                  <SidebarSection
                    id="developers"
                    label={t("nav.developers.dock")}
                    icon="developers"
                    {...sectionProps}
                  >
                    {DEVELOPER_DESTINATIONS.map((item) => (
                      <a
                        key={item.id}
                        href={developerHref(item.hash)}
                        className="sidenav-item"
                        onClick={(event) => closeNavigationOnDeveloperClick(event, item.hash)}
                      >
                        <DestinationGlyph name={item.icon} />
                        <span className="sidenav-item__label">{t(item.labelKey)}</span>
                      </a>
                    ))}
                    {canAudit && (
                      <Link
                        to="/audit"
                        className="sidenav-item"
                        onClick={() => closeNavigation(false)}
                      >
                        <DestinationGlyph name="audit" />
                        <span className="sidenav-item__label">{t("nav.audit")}</span>
                      </Link>
                    )}
                  </SidebarSection>
                ) : null}
              </>
            )}
          </div>

          {helpLink ? (
            <div className="app-sidebar__foot">
              <Link
                to={helpLink as never}
                search={{} as never}
                className="sidenav-item sidenav-item--help"
                aria-current={pathname === helpLink ? "page" : undefined}
                data-active={pathname === helpLink ? "true" : undefined}
                onClick={closeNavigationOnPrimaryClick}
              >
                <DestinationGlyph name="howItWorks" />
                <span className="sidenav-item__label">{t("nav.help")}</span>
              </Link>
            </div>
          ) : null}
        </nav>
      </NavigationFrame>

      <CommandSearch open={searchOpen} destinations={commandDestinations} onClose={closeSearch} />

      <FocusLayoutContext.Provider value={focusHeader}>{children}</FocusLayoutContext.Provider>
    </div>
  );
}

export { MAIN_CONTENT_ID };
